import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { PviNodeWithSpan } from '../../editor/pviTypes';
import type { GraphWarning } from '../../editor/pviGraphService';
import type { SimNodeStatus } from '../../editor/simulationEngine';
import styles from './PviNode.module.css';
import simStyles from './Simulation.module.css';
import advStyles from './Adversarial.module.css';

export interface PviNodeData extends Record<string, unknown> {
  pviNode: PviNodeWithSpan;
  warnings: GraphWarning[];
  highlighted: boolean;
  onHover: (nodeId: number | null) => void;
  onClick: (nodeId: number) => void;
  simStatus?: SimNodeStatus;
  advStatus?: 'failed' | 'warning' | 'passed';
  diagStatus?: 'error' | 'warning';
}

function PviFlowNodeInner({ data }: NodeProps) {
  const { pviNode, warnings, highlighted, onHover, onClick, simStatus, advStatus, diagStatus } = data as unknown as PviNodeData;
  const nt = pviNode.nodeType;

  const typeClass = styles[`node${nt}`] ?? '';

  const partyLabel = pviNode.party.type === 'account'
    ? pviNode.party.address.length > 12
      ? `${pviNode.party.address.slice(0, 6)}...${pviNode.party.address.slice(-4)}`
      : pviNode.party.address
    : null;

  // Simulation state CSS
  const simClass = simStatus === 'active' ? simStyles.nodeActive
    : simStatus === 'completed' ? simStyles.nodeCompleted
    : simStatus === 'failed' ? simStyles.nodeFailed
    : simStatus === 'skipped' ? simStyles.nodeSkipped
    : '';

  // Adversarial state CSS
  const advClass = advStatus === 'failed' ? advStyles.nodeAdvFailed
    : advStatus === 'warning' ? advStyles.nodeAdvWarning
    : advStatus === 'passed' ? advStyles.nodeAdvPassed
    : '';

  // Diagnostic overlay CSS
  const diagClass = diagStatus === 'error' ? styles.diagError
    : diagStatus === 'warning' ? styles.diagWarning
    : '';

  return (
    <div
      className={`${styles.pviNode} ${typeClass} ${highlighted ? styles.highlighted : ''} ${simClass} ${advClass} ${diagClass}`}
      onMouseEnter={() => onHover(pviNode.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick(pviNode.id)}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />

      {warnings.length > 0 && (
        <div className={styles.warningBadge} title={warnings.map((w: GraphWarning) => w.message).join('\n')}>
          ⚠
        </div>
      )}

      <div className={styles.nodeHeader}>
        <span className={styles.nodeType}>{nt}</span>
        <span className={styles.nodeId}>#{pviNode.id}</span>
      </div>

      <div className={styles.nodeBody}>
        {pviNode.assetId && (
          <div className={styles.nodeField}>
            <span className={styles.fieldLabel}>asset</span>
            <span className={styles.fieldValue}>{pviNode.assetId}</span>
          </div>
        )}
        {pviNode.amount !== '0' && (
          <div className={styles.nodeField}>
            <span className={styles.fieldLabel}>amt</span>
            <span className={styles.fieldValue}>{pviNode.amount}</span>
          </div>
        )}
        {partyLabel && (
          <div className={styles.nodeField}>
            <span className={styles.fieldLabel}>{nt === 'HOLD' ? 'from' : 'to'}</span>
            <span className={styles.fieldValue}>{partyLabel}</span>
          </div>
        )}
        {pviNode.label && (
          <div className={styles.nodeField}>
            <span className={styles.fieldValue}>{pviNode.label}</span>
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}

export const PviFlowNode = memo(PviFlowNodeInner);
